import type { CalculationResult } from "../../engines/types";

/**
 * Pure model for the builder's quiet milestone feedback.
 *
 * Two concerns, both React-free and side-effect-free so they can be unit
 * tested without a DOM:
 *
 * 1. `deriveComplianceStatus` folds the rule-bearing slices of an
 *    already-computed `calculateMaterials()` result into one status. It reads
 *    the same `derived.calcData` the workspace badges and the insights panel
 *    read - no second calculation, nothing added to the Zustand store.
 * 2. `advanceMilestoneLatch` decides whether a state change deserves a
 *    celebration. It latches per session so a milestone fires at most once,
 *    and it never fires on the first observation (initial mount).
 *
 * See docs/notes/2026-07-10-workspace-badge-mapper-must-tolerate-partial-calcdata.md
 * - every slice is read defensively; a degraded/partial result yields
 * `unknown`, never a throw.
 */

/**
 * `unknown` - no rule slice present (degraded calc, test mocks). Never a
 * celebration source.
 * `flagged` - at least one rule check raised a warning or an error.
 * `clear` - every rule check the tool runs came back without a flag.
 */
export type ComplianceStatus = "unknown" | "flagged" | "clear";

export interface ComplianceSummary {
	status: ComplianceStatus;
	/** Number of distinct rule checks currently raising a flag. */
	flagCount: number;
}

/**
 * The slices this mapper consults. Partial by design - see the note above.
 *
 * `ventilation` and `cableSpec` are deliberately NOT in this list. Both carry a
 * `warning` that is a pure function of the chosen heater/vent strategy rather
 * than of anything wrong with the design:
 * - `calculateCableSpec` returns a standing note for EVERY wood heater, and one
 *   for every electric heater above 7.5 kW;
 * - `calculateVentilationStrategy` returns the rule-registry guidance text for
 *   the selected strategy ("overvag mekanisk franluft", "kontrollera att
 *   tilluften ar tillracklig").
 * A sweep of 240 realistic configurations (3 engines x wood/electric x sizes x
 * markets) scored 0 clear with those two counted: cableSpec flagged 240/240 and
 * ventilation 120/120 of the electric ones, so the milestone was unreachable.
 * `heaterRequirements` IS in this Pick - it also carries `glassPenalty`-relevant
 * sizing - but its own `.warning` (main-fuse-upgrade advice) is excluded from
 * the flag count below for the identical reason; see the comment at its `if`.
 * See docs/notes/2026-07-28-milestone-feedback-needs-a-mounted-surface-and-a-defect-only-status.md
 */
type ComplianceInput = Partial<
	Pick<
		CalculationResult,
		| "permit"
		| "heaterRequirements"
		| "glassPenalty"
		| "ergonomics"
		| "roofJoists"
		| "electricalBom"
	>
>;

/**
 * Folds the rule slices into one status.
 *
 * Only genuine *defect* signals count as flags. Two categories are excluded:
 * - requirements the design already satisfies (`ventilation.requiresFan`,
 *   `drainage.requiresFloorDrain`) - design facts, not defects;
 * - standing advice attached to a chosen strategy rather than to this design
 *   (see the `ComplianceInput` comment).
 *
 * The test is: could the user act on it and make it go away? A hot-zone
 * electrical component can be moved; "a wood heater needs SiHF cable in the hot
 * zone" cannot.
 */
export function deriveComplianceStatus(
	calcData: ComplianceInput,
): ComplianceSummary {
	const {
		permit,
		heaterRequirements,
		glassPenalty,
		ergonomics,
		roofJoists,
		electricalBom,
	} = calcData;

	const known =
		permit !== undefined ||
		heaterRequirements !== undefined ||
		glassPenalty !== undefined ||
		ergonomics !== undefined ||
		roofJoists !== undefined ||
		electricalBom !== undefined;

	if (!known) {
		return { status: "unknown", flagCount: 0 };
	}

	let flagCount = 0;

	// Permit / placement. NOTE: this is a WIDER reading than the REGLER nav
	// badge, which counts permit alone (workspaceBadges.ts). The badge can
	// therefore show a tick while the milestone still counts flags from the
	// slices below. Collapsing the two onto one shared fold - and better,
	// letting the domain mark each warning advisory-vs-defect instead of a UI
	// mapper deciding slice by slice - is the right depth; see the note.
	if (permit && permit.status !== "ok") flagCount += 1;

	// Heater sizing, including the glass-area penalty.
	//
	// `heaterRequirements.warning` (the main-fuse-upgrade advisory) is
	// deliberately NOT counted, for the same reason as cableSpec/ventilation
	// above: it is a standing note attached to whichever heater tier the
	// volume auto-sized, not a defect in this design. calculateHeaterRequirements
	// was recalibrated to a realistic Swedish fuse ladder (10/16/20/25A instead
	// of a flat 10/16A split), which made a 3-phase load cross the upfuse
	// threshold for ordinary sauna sizes that never triggered it before -
	// "install a bigger fuse for a bigger heater" is not something the design
	// itself got wrong.
	if (

		glassPenalty &&
		glassPenalty.totalGlassAreaSqm > 0 &&
		!glassPenalty.isHeaterSufficient
	) {
		flagCount += 1;
	}

	// The chosen catalog heater is rated for a different room size (issue
	// 159). Unlike the fuse advisory this IS the design's defect: the user
	// picked the unit and can pick another.
	if (
		heaterRequirements?.modelFit &&
		heaterRequirements.modelFit.status !== "ok"
	) {
		flagCount += 1;
	}

	// Bench ergonomics / loyly compliance.
	if (ergonomics && ergonomics.isLoylyCompliant === false) flagCount += 1;
	else if (ergonomics?.warning) flagCount += 1;

	// Roof structure against the snow zone.
	if (roofJoists?.error) flagCount += 1;
	else if (roofJoists?.warning) flagCount += 1;

	// Electrical BOM - hot-zone component placement, which the user can change.
	flagCount += electricalBom?.warnings?.length ?? 0;

	return { status: flagCount > 0 ? "flagged" : "clear", flagCount };
}

/* ── Session latch ──────────────────────────────────── */

/**
 * `unknown` - not observable yet; `pending` - not there; `reached` - milestone
 * condition currently holds.
 */
export type MilestoneState = "unknown" | "pending" | "reached";

export interface MilestoneLatch {
	/** Last observed state, `unknown` before the first observation. */
	previous: MilestoneState;
	/** True once the milestone has fired in this session. */
	celebrated: boolean;
}

export function createMilestoneLatch(): MilestoneLatch {
	return { previous: "unknown", celebrated: false };
}

export interface MilestoneAdvance {
	latch: MilestoneLatch;
	celebrate: boolean;
}

/**
 * Feeds the next observed state into a latch.
 *
 * Fires only on a real `pending -> reached` transition, and only once per
 * session. Consequences, all intentional:
 * - a design that is already complete on page load never celebrates (the
 *   first observation moves `previous` off `unknown` without firing);
 * - an undo/redo bounce (reached -> pending -> reached) does not fire again;
 * - a degraded calculation (`unknown`) mid-session resets the run-up rather
 *   than firing when data returns.
 *
 * Pure: the caller owns the latch (a `useRef` in a component or hook). Nothing
 * about this is persisted to the store or the database - it is session memory.
 */
export function advanceMilestoneLatch(
	latch: MilestoneLatch,
	next: MilestoneState,
): MilestoneAdvance {
	if (latch.previous === next) {
		return { latch, celebrate: false };
	}

	const celebrate =
		next === "reached" && latch.previous === "pending" && !latch.celebrated;

	return {
		latch: {
			previous: next,
			celebrated: latch.celebrated || celebrate,
		},
		celebrate,
	};
}

/**
 * Drops a latch back to `unknown` while keeping its `celebrated` memory.
 *
 * Use when the design is replaced wholesale rather than edited - a saved design
 * resolving, a template being applied. The next observation then re-baselines
 * (`unknown -> reached` never fires), so a jump straight to a complete design
 * is not mistaken for the user having achieved something.
 */
export function resyncMilestoneLatch(latch: MilestoneLatch): MilestoneLatch {
	if (latch.previous === "unknown") return latch;
	return { ...latch, previous: "unknown" };
}

/** Maps a compliance status onto the generic milestone state machine. */
export function complianceMilestoneState(
	status: ComplianceStatus,
): MilestoneState {
	if (status === "unknown") return "unknown";
	return status === "clear" ? "reached" : "pending";
}
